import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { Folder, MessageFull, MessageListItem } from "../types";
import { apiGet, apiPost, apiDelete } from "../api";
import { mockFolders, mockMessages, mockMessageFull } from "../mock";
import type { OpenMode } from "../../config/ui";
import type { Locale } from "../i18n";
import { t } from "../i18n";
import { SvgIcon } from "../components/SvgIcon";
import { isRiskyAttachmentFilename } from "../attachments";
import { ConfirmModal } from "../components/ConfirmModal";
import { ComposeModal } from "../components/ComposeModal";
import { LabelModal, type LabelData } from "../components/LabelModal";
const TRANSPARENT_DRAG_IMAGE = (() => {
  const img = new Image();
  img.src = "data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=";
  return img;
})();
type FoldersResp = { folders: Array<{ name: string; path: string; unread?: number }>; order?: string[] };
type ListResp = {
  folder: string;
  items: Array<{
    uid: number;
    date: string;
    from: Array<{ address: string | null }>;
    subject: string;
    seen: boolean;
    flagged: boolean;
  }>;
  nextCursor: string | null;
};
type MsgResp = {
  uid: number;
  date: string | number;
  from: Array<{ address: string | null }>;
  to: Array<{ address: string | null }>;
  subject: string;
  text: string;
  html?: string | null;
  hasBlockedExternal?: boolean;
  flagged?: boolean;
  trustedSender?: boolean;
  trustedDomain?: boolean;
  attachments?: Array<{ id: string; filename: string | null; contentType: string | null; size: number | null }>;
};

type DataMode = "mock" | "api";

type RailItem = {
  id: string;
  label: string;
  icon: string;
  mapsToFolderId: string;
  color?: string;
};

function useEsc(onEsc: () => void) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onEsc();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onEsc]);
}

function useOutsideClick(ref: React.RefObject<HTMLElement>, onOutside: () => void) {
  useEffect(() => {
    function onDown(e: MouseEvent) {
      const el = ref.current;
      if (!el) return;
      if (e.target instanceof Node && el.contains(e.target)) return;
      onOutside();
    }
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [ref, onOutside]);
}

function formatDateIso(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()} ${d.toLocaleString(undefined, { month: "short" })} ${String(d.getDate()).padStart(2, "0")} ${d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}`;
}

function timeAgo(iso: string): string {
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  if (!Number.isFinite(diffMs) || diffMs < 0) return "";
  const sec = Math.floor(diffMs / 1000);
  const min = Math.floor(sec / 60);
  const hr = Math.floor(min / 60);
  const day = Math.floor(hr / 24);
  const week = Math.floor(day / 7);
  const month = Math.floor(day / 30);
  const year = Math.floor(day / 365);

  if (sec < 60) return `${sec}s ago`;
  if (min < 60) return `${min}m ago`;
  if (hr < 48) return `${hr}h ago`;
  if (day < 14) return `${day}d ago`;
  if (week < 8) return `${week}w ago`;
  if (month < 24) return `${month}mo ago`;
  return `${year}y ago`;
}


function norm(s: string): string {
  return (s ?? "").toLowerCase();
}

function pickFolder(folders: Folder[], kind: "inbox" | "sent" | "drafts" | "spam" | "trash"): Folder | null {
  const byId = new Map(folders.map((f) => [norm(f.id), f]));
  if (kind === "inbox") {
    return byId.get("inbox") ?? byId.get("inbox/") ?? folders.find((f) => norm(f.id) === "inbox") ?? folders.find((f) => norm(f.name) === "inbox") ?? null;
  }

  const needles: Record<string, string[]> = {
    sent: ["sent"],
    drafts: ["draft"],
    spam: ["spam", "junk"],
    trash: ["trash", "deleted"]
  };
  const n = needles[kind] ?? [];

  // Prefer exact-ish name matches, then id/path matches.
  for (const needle of n) {
    const exactName = folders.find((f) => norm(f.name) === needle);
    if (exactName) return exactName;
  }
  for (const needle of n) {
    const nameHit = folders.find((f) => norm(f.name).includes(needle));
    if (nameHit) return nameHit;
  }
  for (const needle of n) {
    const idHit = folders.find((f) => norm(f.id).includes(needle));
    if (idHit) return idHit;
  }
  return null;
}

function buildRail(folders: Folder[]): RailItem[] {
  const items: RailItem[] = [];
  const inbox = pickFolder(folders, "inbox");
  if (inbox) items.push({ id: "INBOX", label: "Inbox", icon: "envelope", mapsToFolderId: inbox.id });

  // Optional “Pinned”
  if (inbox) items.unshift({ id: "PINNED", label: "Pinned Mails", icon: "pin-angle", mapsToFolderId: inbox.id });

  const sent = pickFolder(folders, "sent");
  if (sent) items.push({ id: "SENT", label: "Sent", icon: "send", mapsToFolderId: sent.id });

  const drafts = pickFolder(folders, "drafts");
  if (drafts) items.push({ id: "DRAFTS", label: "Drafts", icon: "pencil", mapsToFolderId: drafts.id });

  const spam = pickFolder(folders, "spam");
  if (spam) items.push({ id: "SPAM", label: "Spam", icon: "envelope-exclamation", mapsToFolderId: spam.id });

  const trash = pickFolder(folders, "trash");
  if (trash) items.push({ id: "TRASH", label: "Trash", icon: "trash", mapsToFolderId: trash.id });

  return items;
}

function applyRailOrder(items: RailItem[], order: string[]): RailItem[] {
  if (!order || order.length === 0) return items;
  const lookup = new Map(items.map((item) => [item.id, item]));
  const ordered: RailItem[] = [];
  for (const id of order) {
    const item = lookup.get(id);
    if (item) {
      ordered.push(item);
      lookup.delete(id);
    }
  }
  if (lookup.size > 0) {
    for (const item of items) {
      if (lookup.has(item.id)) {
        ordered.push(item);
      }
    }
  }
  return ordered;
}

function reorderRailIds(order: string[], sourceId: string, targetId: string, insertAfter: boolean = false): string[] {
  const next = order.filter((id) => id !== sourceId);
  const targetIndex = next.indexOf(targetId);
  if (targetIndex === -1) {
    next.push(sourceId);
    return next;
  }
  const insertIndex = targetIndex + (insertAfter ? 1 : 0);
  next.splice(insertIndex, 0, sourceId);
  return next;
}

export function MailView(props: {
  locale: Locale;
  dataMode: DataMode;
  openMode: OpenMode;
  onOpenMessage: (id: string) => void;

  authedEmail: string;
  userFullName: string | null;
  onLogout: () => void;
  onSetOpenMode: (m: OpenMode) => void;
  onSetDataMode: (m: DataMode) => void;
}) {
  const [folders, setFolders] = useState<Folder[]>([]);
  const refreshFolders = useCallback(async () => {
    if (props.dataMode !== "api") return;
    const res = await apiGet<FoldersResp>("/v1/folders");
    if (!res.ok) return;
    const mapped = res.data.folders.map((f) => ({ id: f.path, name: f.name, unread: f.unread ?? 0 }));
    setFolders(mapped);
    const savedOrder = Array.isArray(res.data.order) ? res.data.order : null;
    setFolderOrder(savedOrder && savedOrder.length > 0 ? savedOrder : null);
  }, [props.dataMode]);
  const [railLoading, setRailLoading] = useState<boolean>(props.dataMode === "api");

  // Custom labels
  const [labels, setLabels] = useState<LabelData[]>([]);
  const [labelModalOpen, setLabelModalOpen] = useState(false);
  const [editingLabel, setEditingLabel] = useState<LabelData | undefined>(undefined);

  const refreshLabels = useCallback(async () => {
    if (props.dataMode !== "api") return;
    const res = await apiGet<{ labels: LabelData[] }>("/v1/labels");
    if (res.ok) setLabels(res.data.labels);
  }, [props.dataMode]);

  const baseRailItems = useMemo(() => {
    const system = buildRail(folders.length ? folders : mockFolders);
    const labelItems: RailItem[] = labels.map((l) => ({
      id: `LABEL_${l.id}`,
      label: l.imapFolder,
      icon: l.icon,
      mapsToFolderId: l.imapFolder,
      color: l.color
    }));
    return [...system, ...labelItems];
  }, [folders, labels]);
  const [folderOrder, setFolderOrder] = useState<string[] | null>(null);
  const [previewOrder, setPreviewOrder] = useState<string[] | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const dragOverRef = useRef<string | null>(null);
  const dragInsertAfterRef = useRef<boolean>(false);
  const itemRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const positionCache = useRef<Map<string, number>>(new Map());
  const layoutFrame = useRef<number | null>(null);
  const baseOrder = useMemo(() => baseRailItems.map((item) => item.id), [baseRailItems]);
  const resolvedOrder = folderOrder && folderOrder.length ? folderOrder : baseOrder;
  const displayOrder = previewOrder ?? resolvedOrder;
  const orderedRailItems = useMemo(() => applyRailOrder(baseRailItems, displayOrder), [baseRailItems, displayOrder]);
  const [railId, setRailId] = useState<string>("INBOX");
  useLayoutEffect(() => {
    if (layoutFrame.current !== null) {
      cancelAnimationFrame(layoutFrame.current);
      layoutFrame.current = null;
    }
    const newPositions = new Map<string, number>();
    itemRefs.current.forEach((el, id) => {
      newPositions.set(id, el.getBoundingClientRect().top);
    });
    const animated: HTMLDivElement[] = [];
    newPositions.forEach((top, id) => {
      const prevTop = positionCache.current.get(id);
      if (prevTop === undefined) return;
      if (prevTop === top) return;
      if (draggingId && draggingId === id) return;
      const el = itemRefs.current.get(id);
      if (!el) return;
      const delta = prevTop - top;
      if (delta === 0) return;
      el.style.transition = "none";
      el.style.transform = `translateY(${delta}px)`;
      el.getBoundingClientRect();
      animated.push(el);
    });
    positionCache.current = newPositions;
    if (animated.length > 0) {
      layoutFrame.current = requestAnimationFrame(() => {
        animated.forEach((el) => {
          el.style.transition = "transform 0.25s cubic-bezier(0.2,0.15,0.25,1)";
          el.style.transform = "";
        });
        layoutFrame.current = null;
      });
    }
    return () => {
      if (layoutFrame.current !== null) {
        cancelAnimationFrame(layoutFrame.current);
        layoutFrame.current = null;
      }
    };
  }, [displayOrder, draggingId]);
  const [search, setSearch] = useState<string>("");
  const [serverResults, setServerResults] = useState<MessageListItem[]>([]);
  const [searchingServer, setSearchingServer] = useState<boolean>(false);
  const searchController = useRef<AbortController | null>(null);
  const searchTimer = useRef<number | null>(null);

  const [composeOpen, setComposeOpen] = useState<boolean>(false);
  const [composePrefill, setComposePrefill] = useState<{ to: string; subject: string; text: string } | null>(null);
  const [contacts, setContacts] = useState<{ email: string; name: string | null }[]>([]);


  // Pinned is stored as IMAP \\Flagged (server-side).

  const folderId = useMemo(
    () => orderedRailItems.find((r) => r.id === railId)?.mapsToFolderId ?? "INBOX",
    [railId, orderedRailItems]
  );

  // Debounced server-side search (fires 300ms after typing stops, 2+ chars)
  useEffect(() => {
    const q = search.trim();
    if (q.length < 2 || props.dataMode !== "api") {
      if (searchTimer.current) { window.clearTimeout(searchTimer.current); searchTimer.current = null; }
      searchController.current?.abort();
      setServerResults([]);
      setSearchingServer(false);
      return;
    }

    if (searchTimer.current) window.clearTimeout(searchTimer.current);
    searchTimer.current = window.setTimeout(() => {
      searchController.current?.abort();
      const controller = new AbortController();
      searchController.current = controller;
      setSearchingServer(true);

      apiGet<{ items: Array<{ uid: number; date: string; from: Array<{ address: string | null }>; subject: string; seen: boolean; flagged: boolean }> }>(
        `/v1/search?q=${encodeURIComponent(q)}&folder=${encodeURIComponent(folderId)}&limit=50`,
        { signal: controller.signal }
      ).then((res) => {
        if (controller.signal.aborted) return;
        if (res.ok) {
          setServerResults(res.data.items.map((m) => ({
            id: String(m.uid),
            from: (m.from?.[0]?.address ?? "") || "",
            subject: m.subject ?? "",
            dateIso: typeof m.date === "string" ? m.date : new Date(m.date).toISOString(),
            snippet: "",
            unread: m.seen === true ? false : true,
            flagged: m.flagged === true
          })));
        }
        setSearchingServer(false);
      }).catch(() => {
        if (!controller.signal.aborted) setSearchingServer(false);
      });
    }, 300);

    return () => {
      if (searchTimer.current) { window.clearTimeout(searchTimer.current); searchTimer.current = null; }
      searchController.current?.abort();
    };
  }, [search, folderId, props.dataMode]);

  useEffect(() => {
    if (orderedRailItems.length === 0) return;
    const exists = orderedRailItems.some((r) => r.id === railId);
    if (!exists) setRailId(orderedRailItems[0].id);
  }, [orderedRailItems, railId]);

  const [messages, setMessages] = useState<MessageListItem[]>([]);
  const [loadingMessages, setLoadingMessages] = useState<boolean>(false);
  const listController = useRef<AbortController | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [full, setFull] = useState<MessageFull | null>(null);
  const readerRequestId = useRef(0);
  const readerController = useRef<AbortController | null>(null);


  const [suppressAutoSeenUntil, setSuppressAutoSeenUntil] = useState<{ id: string; untilMs: number } | null>(null);
  const [settingsOpen, setSettingsOpen] = useState<boolean>(false);
  const popRef = useRef<HTMLDivElement | null>(null);
  useOutsideClick(popRef as any, () => setSettingsOpen(false));

  type RepData = {
    overall: number;
    tier: string;
    categories: {
      activity: number;
      contentQuality: number;
      behaviour: number;
      accountHistory: number;
      reputation: number;
    };
  };
  const REP_DEFAULT: RepData = {
    overall: 5.0,
    tier: "", // populated by first poll or settings open
    categories: { activity: 5.0, contentQuality: 5.0, behaviour: 5.0, accountHistory: 5.0, reputation: 5.0 },
  };
  const [repData, setRepData] = useState<RepData>(REP_DEFAULT);
  const [repExpanded, setRepExpanded] = useState(false);
  const repFetched = useRef(false);
  const [accountSuspended, setAccountSuspended] = useState(false);
  // Tracks the last server-returned timestamp for efficient since= polling.
  const lastUpdatesTs = useRef<string | null>(null);
  // Database-driven tier names for display
  const [tierNames, setTierNames] = useState<Record<number, string>>({
    0: "New",
    1: "Established",
    2: "Standard",
    3: "Trusted",
    4: "Unlimited",
  });

  // Unified background polling — replaces individual /folders + /reputation polls.
  useEffect(() => {
    if (props.dataMode !== "api") return;
    let alive = true;

    type UpdatesResp = {
      timestamp: string;
      changes: {
        subscription?: { status: string; current_period_end: string | null };
        reputation?: {
          overall_score: number;
          categories: Record<string, number>;
          status: string;
        };
        domains?: Array<{ domain: string; status: string; mx_ok: boolean; spf_ok: boolean; dkim_ok: boolean }>;
      };
      tierNames?: Record<number, string>;
    };

    const tick = async () => {
      if (!alive) return;
      const since = lastUpdatesTs.current;
      const url = since ? `/v1/updates?since=${encodeURIComponent(since)}` : "/v1/updates";
      const res = await apiGet<UpdatesResp>(url);
      if (!alive || !res.ok) return;

      lastUpdatesTs.current = res.data.timestamp;
      const { changes, tierNames: newTierNames } = res.data;

      // Update tier names from the API (loaded once on first poll)
      if (newTierNames) setTierNames(newTierNames);

      if (changes.subscription) {
        setAccountSuspended(changes.subscription.status === "suspended");
      }

      if (changes.reputation) {
        const r = changes.reputation as any;
        setRepData({
          overall: r.overall_score,
          tier: r.tier ?? repData.tier,
          categories: {
            activity: r.categories.activity ?? 5.0,
            contentQuality: r.categories.content_quality ?? 5.0,
            behaviour: r.categories.behaviour ?? 5.0,
            accountHistory: r.categories.account_history ?? 5.0,
            reputation: r.categories.reputation ?? 5.0,
          },
        });
        // Mark as fetched since polling now returns tier data too
        repFetched.current = true;
      }
    };

    // Immediate first poll, then every 15s
    void tick();
    const t = window.setInterval(tick, 15_000);
    return () => {
      alive = false;
      window.clearInterval(t);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.dataMode]);

  // Fetch full reputation (with tier) when settings open if not yet populated by polling.
  useEffect(() => {
    if (!settingsOpen || repFetched.current || props.dataMode !== "api") return;
    repFetched.current = true;
    apiGet<any>("/v1/account/reputation").then((res) => {
      if (res.ok) {
        const data = res.data;
        setRepData({
          overall: data.overall_score ?? 5.0,
          tier: data.tier ?? "New",
          categories: {
            activity: data.categories?.activity ?? 5.0,
            contentQuality: data.categories?.content_quality ?? 5.0,
            behaviour: data.categories?.behaviour ?? 5.0,
            accountHistory: data.categories?.account_history ?? 5.0,
            reputation: data.categories?.reputation ?? 5.0,
          },
        });
      }
    }).catch(() => {});
  }, [settingsOpen, props.dataMode]);

  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const [readerMenuOpen, setReaderMenuOpen] = useState<boolean>(false);

  const [attachConfirm, setAttachConfirm] = useState<{ url: string } | null>(null);
  const [linkConfirm, setLinkConfirm] = useState<{ url: string } | null>(null);
  const [showExternalImages, setShowExternalImages] = useState<boolean>(false);
  const [externalDropdownOpen, setExternalDropdownOpen] = useState<boolean>(false);
  const [trustedSender, setTrustedSender] = useState<boolean>(false);
  const [trustedDomain, setTrustedDomain] = useState<boolean>(false);
  const externalDropdownRef = useRef<HTMLDivElement | null>(null);
  useOutsideClick(externalDropdownRef as any, () => setExternalDropdownOpen(false));
  const menuRef = useRef<HTMLDivElement | null>(null);
  useOutsideClick(menuRef as any, () => { setMenuOpenId(null); setReaderMenuOpen(false); });

  function getInitials(): string {
    // Try to get initials from full name first
    if (props.userFullName) {
      const parts = props.userFullName.trim().split(/\s+/).filter(p => p.length > 0);
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      }
      // Single name: use first letter
      return props.userFullName[0].toUpperCase();
    }
    // Fall back to email address (local part only)
    const localPart = props.authedEmail.split("@")[0];
    const parts = localPart.split(/[._\-]+/).filter(p => p.length > 0);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    // Single word: use first letter
    return localPart[0].toUpperCase();
  }

  function RepTooltip({ text, children, block }: { text: string[]; children: React.ReactNode; block?: boolean }) {
    const [coords, setCoords] = React.useState<{ top: number; left: number } | null>(null);
    const anchorRef = React.useRef<HTMLSpanElement | null>(null);
    const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
    const TIP_WIDTH = 220;

    function show() {
      if (!anchorRef.current) return;
      const rect = anchorRef.current.getBoundingClientRect();
      // Anchor right edge of tooltip to right edge of element; clamp to viewport left
      const left = Math.max(8, rect.right - TIP_WIDTH);
      setCoords({ top: rect.bottom + 8, left });
    }

    return (
      <span
        ref={anchorRef}
        style={{ display: block ? "block" : "inline-flex", alignItems: block ? undefined : "center" }}
        onMouseEnter={() => { timerRef.current = setTimeout(show, 400); }}
        onMouseLeave={() => { if (timerRef.current) clearTimeout(timerRef.current); setCoords(null); }}
      >
        {children}
        {coords && (
          <span style={{
            position: "fixed", top: coords.top, left: coords.left,
            background: "#1a1a1a", color: "#f5f5f5", fontSize: "11px", lineHeight: "1.5",
            padding: "8px 10px", borderRadius: "6px", width: TIP_WIDTH, whiteSpace: "normal",
            boxShadow: "0 4px 12px rgba(0,0,0,0.35)", zIndex: 9999, pointerEvents: "none",
          }}>
            {text.map((p, i) => (
              <span key={i} style={{ display: "block", marginBottom: i < text.length - 1 ? "6px" : 0 }}>
                {p.split("\n").map((line, j, arr) => (
                  <React.Fragment key={j}>{line}{j < arr.length - 1 && <br />}</React.Fragment>
                ))}
              </span>
            ))}
          </span>
        )}
      </span>
    );
  }

  function scoreColor(n: number): string {
    if (n < 2.5) return "#ef4444";  // red
    if (n < 4.5) return "#f59e0b";  // yellow
    return "#10b981";               // green
  }

  function getReputationStatus(data: RepData): "OK" | "WARNING" | "DANGER" {
    // Threshold-based status: worst condition wins (DANGER > WARNING > OK).
    // Simple "less than" comparisons, no ranges.
    const minCategory = Math.min(...Object.values(data.categories));

    // DANGER: Critical thresholds
    if (data.overall < 3.5 || minCategory < 1.5) return "DANGER";

    // WARNING: Caution thresholds
    if (data.overall < 4.6 || minCategory < 3.0) return "WARNING";

    // OK: All above thresholds
    return "OK";
  }

  function getReputationStatusMessage(data: RepData): string {
    const status = getReputationStatus(data);
    const minCategory = Math.min(...Object.values(data.categories));

    if (status === "DANGER") {
      if (minCategory < 1.5) return "At risk of suspension";
      return "At risk of downgrade";
    }

    if (status === "WARNING") {
      // If a category is below 3.0, show its specific message
      if (data.categories.activity < 3.0) return "Unusual activity levels";
      if (data.categories.contentQuality < 3.0) return "Outgoing mail issues";
      if (data.categories.behaviour < 3.0) return "Usage patterns differ";
      if (data.categories.accountHistory < 3.0) return "Repeated offenses stacking up";
      if (data.categories.reputation < 3.0) return "Account is flagged";
      // Otherwise, it's overall score that's low
      if (data.overall < 4.5) return "Tier advancement blocked";
      return "Nearing advancement threshold requirement";
    }

    return "";
  }

  function getStatusColor(data: RepData): string {
    const status = getReputationStatus(data);
    if (status === "DANGER") return "#ef4444";  // red
    if (status === "WARNING") return "#f59e0b"; // yellow
    return "#10b981";                           // green
  }

  function getStatusTooltip(data: RepData): string[] {
    const status = getReputationStatus(data);
    const thresholds = [
      "Account Standing Thresholds:",
      "– Tier advancement: Overall ≥4.5 AND all categories ≥2.5",
      "– Tier downgrade:   Overall <3.0 OR any category <2.0",
      "– Account suspension: Any category <1.0",
      "Early warning zones (before consequences):",
      "– WARNING: Overall <4.6 OR any category <3.0 (approaching limits)",
      "– DANGER:  Overall <3.5 OR any category <1.5 (risk of consequences)",
    ];
    if (status === "OK") {
      return [...thresholds, "Current: All scores are healthy. No action needed."];
    }
    if (status === "WARNING") {
      return [...thresholds, "Current: You can still advance tiers if overall ≥4.5 and all categories ≥2.5, but you are in the early warning zone. Improve scores for a better safety margin."];
    }
    // DANGER
    return [...thresholds, "Current: One or more scores have crossed a critical threshold. Tier downgrade or account suspension may apply. Immediate improvement required."];
  }

  const activeRowRef = useRef<HTMLDivElement | null>(null);

  useEsc(() => {
    if (props.openMode === "in-list" && activeId) {
      setActiveId(null);
      setFull(null);
    }
    if (settingsOpen) setSettingsOpen(false);
    if (menuOpenId) setMenuOpenId(null);
    if (readerMenuOpen) setReaderMenuOpen(false);
  });

  useEffect(() => {
    let alive = true;
    if (props.dataMode === "mock") {
      setFolders(mockFolders);
      setRailLoading(false);
      return;
    }
    setRailLoading(true);
    (async () => {
      try {
        const [foldersRes, , contactsRes] = await Promise.all([
          apiGet<FoldersResp>("/v1/folders"),
          refreshLabels(),
          apiGet<{ contacts: { email: string; name: string | null }[] }>("/v1/contacts")
        ]);
        if (!alive) return;
        if (foldersRes.ok) {
          const mapped = foldersRes.data.folders.map((f) => ({ id: f.path, name: f.name, unread: f.unread ?? 0 }));
          setFolders(mapped);
          const savedOrder = Array.isArray(foldersRes.data.order) ? foldersRes.data.order : null;
          setFolderOrder(savedOrder && savedOrder.length > 0 ? savedOrder : null);
        } else {
          setFolders([]);
        }
        if (contactsRes.ok) {
          setContacts(contactsRes.data.contacts);
        }
      } finally {
        if (alive) setRailLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [props.dataMode, refreshLabels]);

  const unreadByFolder = useMemo(() => {
    const m = new Map<string, number>();
    for (const f of folders) m.set(f.id, f.unread);
    return m;
  }, [folders]);

  useEffect(() => {
    setActiveId(null);
    setFull(null);
    setMessages([]);

    listController.current?.abort();
    readerController.current?.abort();

    if (props.dataMode === "mock") {
      setMessages(mockMessages(folderId));
      setLoadingMessages(false);
      return;
    }

    setLoadingMessages(true);
    const controller = new AbortController();
    listController.current = controller;
    const signal = controller.signal;

    (async () => {
      try {
        const res = await apiGet<ListResp>(`/v1/messages?folder=${encodeURIComponent(folderId)}`, { signal });
        if (signal.aborted) return;
        if (res.ok) {
          const mapped = res.data.items.map((m) => ({
            id: String(m.uid),
            from: (m.from?.[0]?.address ?? "") || "",
            subject: m.subject ?? "",
            dateIso: typeof m.date === "string" ? m.date : new Date(m.date).toISOString(),
            snippet: "",
            unread: (m.seen === true) ? false : true,
            flagged: m.flagged === true
          }));
          setMessages(mapped);
        }
        else setMessages([]);
        setLoadingMessages(false);
      } catch (err) {
        if (signal.aborted) return;
        setMessages([]);
        setLoadingMessages(false);
      }
    })();

    return () => {
      controller.abort();
    };
  }, [props.dataMode, folderId]);

  useEffect(() => {
    if (props.dataMode !== "api") return;
    if (railId === "PINNED") return;

    let alive = true;
    const intervalMs = 15000;

    const tick = async () => {
      const res = await apiGet<ListResp>(`/v1/messages?folder=${encodeURIComponent(folderId)}`);
      if (!alive) return;
      if (!res.ok) return;

      const mapped = res.data.items.map((m) => ({
        id: String(m.uid),
        from: (m.from?.[0]?.address ?? "") || "",
        subject: m.subject ?? "",
        dateIso: typeof m.date === "string" ? m.date : new Date(m.date).toISOString(),
        snippet: "",
        unread: (m.seen === true) ? false : true,
          flagged: m.flagged === true
      }));

      setMessages((prev) => {
        const seen = new Set(prev.map((x) => x.id));
        const added = mapped.filter((x) => !seen.has(x.id));
        if (added.length === 0) return prev;
        return [...added, ...prev];
      });
    };

    const t = window.setInterval(tick, intervalMs);
    return () => {
      alive = false;
      window.clearInterval(t);
    };
  }, [props.dataMode, folderId, railId]);

  useEffect(() => {
    if (!activeId) return;
    setShowExternalImages(false);
    setTrustedSender(false);
    setTrustedDomain(false);
    setFull(null);

    if (props.dataMode === "api") {
      const uid = Number(activeId);
      if (!(suppressAutoSeenUntil && suppressAutoSeenUntil.id === String(uid) && Date.now() < suppressAutoSeenUntil.untilMs)) {
        void apiPost<{ ok: true }>(`/v1/messages/${encodeURIComponent(String(uid))}/seen`, { folder: folderId, seen: true });
      }
      void refreshFolders();
      setMessages((prev) => prev.map((m) => (m.id === String(uid) ? { ...m, unread: false } : m)));
    }

    if (props.dataMode === "mock") {
      setFull(mockMessageFull(activeId));
      return;
    }

    readerRequestId.current += 1;
    const currentRequest = readerRequestId.current;
    readerController.current?.abort();
    const controller = new AbortController();
    readerController.current = controller;
    const signal = controller.signal;

    (async () => {
      const uid = Number(activeId);
      try {
        const res = await apiGet<MsgResp>(`/v1/messages/${encodeURIComponent(String(uid))}?folder=${encodeURIComponent(folderId)}`, { signal });
        if (!res.ok) {
          if (!signal.aborted) {
            setFull(null);
          }
          return;
        }

        const fromAddr = res.data.from?.[0]?.address ?? "";
        const toAddr = Array.isArray(res.data.to)
          ? res.data.to.map((x) => x?.address ?? "").filter((x): x is string => typeof x === "string" && x.length > 0).join(", ")
          : "";
        const bodyText = typeof res.data.text === "string" && res.data.text.length > 0
          ? res.data.text
          : (typeof res.data.html === "string" ? res.data.html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim() : "");
        const bodyHtml = typeof res.data.html === "string" && res.data.html.length > 0 ? res.data.html : null;
        if (currentRequest !== readerRequestId.current) {
          return;
        }
        setFull({
          id: String(res.data.uid ?? uid),
          from: fromAddr,
          to: toAddr,
          subject: res.data.subject ?? "",
          dateIso: typeof res.data.date === "string" ? res.data.date : new Date(res.data.date).toISOString(),
          text: bodyText,
          html: bodyHtml,
          hasBlockedExternal: res.data.hasBlockedExternal === true,
          attachments: Array.isArray(res.data.attachments) ? res.data.attachments : []
        });
        const isTrSender = res.data.trustedSender === true;
        const isTrDomain = res.data.trustedDomain === true;
        setTrustedSender(isTrSender);
        setTrustedDomain(isTrDomain);
        if (isTrSender || isTrDomain) {
          setShowExternalImages(true);
        }
      } catch (err) {
        if (signal.aborted) {
          return;
        }
        console.error("Failed loading message", err);
        setFull(null);
      }
    })();

    return () => {
      if (readerController.current === controller) {
        readerController.current = null;
      }
      controller.abort();
    };
  }, [props.dataMode, activeId, folderId]);

  useEffect(() => {
    if (!activeId) return;
    activeRowRef.current?.scrollIntoView({ block: "nearest" });
  }, [activeId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = messages;

    if (railId === "PINNED") {
      list = list.filter((m) => m.flagged === true);
    }

    if (!q) return list;

    // Client-side filter on loaded messages
    const clientMatches = list.filter((m) =>
      m.subject.toLowerCase().includes(q) ||
      m.from.toLowerCase().includes(q) ||
      m.snippet.toLowerCase().includes(q)
    );

    // Merge with server results (dedup by id, client wins for freshness)
    if (serverResults.length === 0) return clientMatches;

    const seen = new Set(clientMatches.map((m) => m.id));
    const extra = serverResults.filter((m) => !seen.has(m.id));
    if (extra.length === 0) return clientMatches;

    // Merge and sort newest first
    return [...clientMatches, ...extra].sort((a, b) =>
      new Date(b.dateIso).getTime() - new Date(a.dateIso).getTime()
    );
  }, [messages, search, railId, serverResults]);
  const showLoading = props.dataMode === "api" && loadingMessages;
  const showEmpty = props.dataMode === "api" && !loadingMessages && filtered.length === 0;

  async function persistRailOrder(order: string[]) {
    if (props.dataMode !== "api") return;
    const res = await apiPost<{ ok: true }>("/v1/folders/order", { order });
    if (!res.ok) {
      console.warn("Unable to persist rail order", res.error);
    }
  }

  function handleDragStart(event: React.DragEvent<HTMLDivElement>, id: string) {
    setDraggingId(id);
    dragOverRef.current = null;
    dragInsertAfterRef.current = false;
    const dt = event.dataTransfer;
    if (!dt) return;
    dt.setData("text/plain", id);
    dt.setDragImage(TRANSPARENT_DRAG_IMAGE, 0, 0);
    dt.effectAllowed = "move";
  }

  function updateDragTarget(event: React.DragEvent<HTMLDivElement>, id: string) {
    event.preventDefault();
    if (!draggingId || draggingId === id) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const insertAfter = event.clientY >= rect.top + rect.height / 2;
    if (dragOverRef.current === id && dragInsertAfterRef.current === insertAfter) return;
    dragOverRef.current = id;
    dragInsertAfterRef.current = insertAfter;
    const nextOrder = reorderRailIds(displayOrder, draggingId, id, insertAfter);
    setPreviewOrder(nextOrder);
  }

  function handleDragOver(event: React.DragEvent<HTMLDivElement>, id: string) {
    updateDragTarget(event, id);
  }

  function handleDrop(event: React.DragEvent<HTMLDivElement>, overId: string) {
    event.preventDefault();
    if (!draggingId || draggingId === overId) {
      setPreviewOrder(null);
      return;
    }
    const nextOrder = reorderRailIds(displayOrder, draggingId, overId, dragInsertAfterRef.current);
    setFolderOrder(nextOrder);
    setPreviewOrder(null);
    setDraggingId(null);
    dragOverRef.current = null;
    dragInsertAfterRef.current = false;
    void persistRailOrder(nextOrder);
  }

  function handleDragEnd() {
    setDraggingId(null);
    setPreviewOrder(null);
    dragOverRef.current = null;
    dragInsertAfterRef.current = false;
  }

  function handleAddLabel() {
    setEditingLabel(undefined);
    setLabelModalOpen(true);
  }

  function handleEditLabel(label: LabelData) {
    setEditingLabel(label);
    setLabelModalOpen(true);
  }

  async function handleDeleteLabel(label: LabelData) {
    if (!label.id) return;
    const ok = window.confirm(`Delete label "${label.imapFolder}"? Messages in this folder will be lost.`);
    if (!ok) return;
    await apiDelete<{ ok: boolean }>(`/v1/labels/${label.id}`);
    await refreshLabels();
    // If viewing the deleted label's folder, switch to inbox
    if (railId === `LABEL_${label.id}`) setRailId("INBOX");
  }

  function handleLabelSaved() {
    refreshLabels();
  }


  function onRowClick(id: string) {
    if (props.openMode === "in-list") {
      if (activeId === id) {
        setActiveId(null);
        setFull(null);
      } else {
        setActiveId(id);
      }
      return;
    }
    props.onOpenMessage(id);
  }

  
  function startDownload(url: string) {
    // Use a normal navigation to keep cookies; open in new tab.
    window.open(url, "_blank", "noopener,noreferrer");
  }

  function onDownloadAttachment(attId: string, filename: string | null) {
    const url = `/api/v1/attachments/${encodeURIComponent(attId)}?folder=${encodeURIComponent(folderId)}`;
    const name = filename ?? "";
    if (isRiskyAttachmentFilename(name)) {
      setAttachConfirm({ url });
      return;
    }
    startDownload(url);
  }
function closeInline() {
    setActiveId(null);
    setFull(null);
  }
  

  function printInline() {
    // MVP: print current page. Later we can open a print-friendly view.
    window.print();
  }
function openCompose() {
    setComposePrefill(null);
    setComposeOpen(true);
  }
  function closeCompose() {
    setComposeOpen(false);
  }

  async function saveDraftFromCompose(draft: { to: string; subject: string; text: string }) {
    if (props.dataMode !== "api") return;
    await apiPost<{ ok: true }>("/v1/drafts", {
      to: draft.to,
      subject: draft.subject,
      text: draft.text
    });
  }

  async function sendFromCompose(msg: { from: string; to: string[]; subject: string; text: string }) {
    if (props.dataMode !== "api") return;
    await apiPost<{ ok: true }>("/v1/send", {
      from: msg.from,
      to: msg.to,
      cc: [],
      bcc: [],
      subject: msg.subject,
      text: msg.text
    });
  }


  function quoteBody(fullText: string): string {
    const lines = (fullText ?? "").split("\n");
    return lines.map((l) => `> ${l}`).join("\n");
  }

  function normalizeSubject(prefix: string, subject: string): string {
    const s = (subject ?? "").trim();
    const low = s.toLowerCase();
    if (low.startsWith(prefix.toLowerCase())) return s;
    return `${prefix}${s.length ? " " : ""}${s}`;
  }

  function parseEmails(raw: string): string[] {
    const parts = (raw ?? "")
      .split(/[;,\n]/g)
      .map((x) => x.trim())
      .filter((x) => x.length > 0);
    return parts;
  }

  function openReply(kind: "reply" | "replyAll" | "forward") {
    if (!full || !activeId) return;

    const subj =
      kind === "forward"
        ? normalizeSubject("Fwd:", full.subject)
        : normalizeSubject("Re:", full.subject);

    const quoted = quoteBody(full.text ?? "");
    const header = `\n\n---\nFrom: ${full.from}\nTo: ${full.to}\nDate: ${new Date(full.dateIso).toLocaleString()}\nSubject: ${full.subject}\n\n`;

    if (kind === "forward") {
      setComposePrefill({ to: "", subject: subj, text: header + quoted });
      setComposeOpen(true);
      return;
    }

    const toFrom = full.from || "";
    const toList = parseEmails(full.to);
    const me = props.authedEmail.toLowerCase();

    // "Reply all" heuristic: include other recipients (excluding me + sender)
    let all = [toFrom, ...toList];
    all = all
      .map((x) => x.trim())
      .filter((x) => x.length > 0)
      .filter((x) => x.toLowerCase() != me)
      .filter((x) => x.toLowerCase() != toFrom.toLowerCase());

    const replyAllTo = [toFrom, ...all].filter((x) => x.length > 0).join(", ");

    const to = kind === "replyAll" ? replyAllTo : toFrom;

    setComposePrefill({ to, subject: subj, text: "\n\n" + quoted });
    setComposeOpen(true);
  }

  async function markUnread(id: string) {
    if (props.dataMode !== "api") return;

    const uid = Number(id);
    await apiPost<{ ok: true }>(`/v1/messages/${encodeURIComponent(String(uid))}/seen`, {
      folder: folderId,
      seen: false
    });

    setSuppressAutoSeenUntil({ id: String(uid), untilMs: Date.now() + 5000 });
    setMessages((prev) => prev.map((m) => (m.id === String(uid) ? { ...m, unread: true } : m)));
    void refreshFolders();
  }

  async function togglePinned(id: string) {
    if (props.dataMode !== "api") return;

    const uid = Number(id);
    const current: any = messages.find((m: any) => m.id === id);
    const next = !(current?.flagged === true);

    setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, flagged: next } : m)));

    // Keep the expanded reader state in sync with list state
    setFull((prev) => {
      if (!prev) return prev;
      if (String(prev.uid ?? prev.id) !== id) return prev;
      return { ...prev, flagged: next };
    });

    await apiPost<{ ok: true }>(`/v1/messages/${encodeURIComponent(String(uid))}/flagged`, {
      folder: folderId,
      flagged: next
    });
  }

  async function toggleTrustSender() {
    if (!full?.from) return;
    const next = !trustedSender;
    setTrustedSender(next);
    if (next) setShowExternalImages(true);
    if (props.dataMode === "api") {
      if (next) {
        await apiPost<{ ok: true }>("/v1/image-trust", { ruleType: "sender", value: full.from });
      } else {
        await apiDelete<{ ok: true }>("/v1/image-trust", { ruleType: "sender", value: full.from });
      }
    }
  }

  async function toggleTrustDomain() {
    if (!full?.from) return;
    const domain = full.from.includes("@") ? full.from.split("@")[1] : null;
    if (!domain) return;
    const next = !trustedDomain;
    setTrustedDomain(next);
    if (next) setShowExternalImages(true);
    if (props.dataMode === "api") {
      if (next) {
        await apiPost<{ ok: true }>("/v1/image-trust", { ruleType: "domain", value: domain });
      } else {
        await apiDelete<{ ok: true }>("/v1/image-trust", { ruleType: "domain", value: domain });
      }
    }
  }

function onMenuAction(action: string, m: MessageListItem) {
    // placeholder only
    console.log("action:", action, "message:", m.id);
    setMenuOpenId(null);
  }
  const listTitle = useMemo(
    () => orderedRailItems.find((r) => r.id === railId)?.label ?? "Inbox",
    [orderedRailItems, railId]
  );
  return (
    <div className="shell">
      {accountSuspended && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 9999,
          background: "rgba(0,0,0,0.82)",
          display: "flex", alignItems: "center", justifyContent: "center"
        }}>
          <div style={{
            background: "var(--bg-surface, #1a1a1a)",
            border: "1px solid var(--border, #333)",
            borderRadius: 12, padding: "40px 48px",
            maxWidth: 480, textAlign: "center"
          }}>
            <div style={{ fontSize: 36, marginBottom: 16 }}>⚠️</div>
            <div style={{ fontSize: 20, fontWeight: 600, marginBottom: 12, color: "var(--color-danger, #e55)" }}>
              Account Suspended
            </div>
            <div style={{ color: "var(--fg-muted, #999)", marginBottom: 28, lineHeight: 1.5 }}>
              Your account has been suspended. You cannot send or receive mail at this time.
              Please contact support to resolve this issue.
            </div>
            <button
              onClick={props.onLogout}
              style={{
                padding: "10px 24px", borderRadius: 6,
                background: "var(--bg-hover, #2a2a2a)",
                border: "1px solid var(--border, #333)",
                color: "var(--fg, #eee)", cursor: "pointer", fontSize: 14
              }}
            >
              Log out
            </button>
          </div>
        </div>
      )}
      <div className="rail">
        {railLoading ? (
          <div className="railPlaceholder">
            <div className="railPlaceholderSpinner" aria-label="Loading rail" role="status"></div>
            <div className="railPlaceholderText">Loading…</div>
          </div>
        ) : (
          <>
            {orderedRailItems.map((r) => {
              const isActive = r.id === railId;
              const unread = unreadByFolder.get(r.mapsToFolderId) ?? 0;
              const isDraggingItem = draggingId === r.id;
              const isLabel = r.id.startsWith("LABEL_");
              const labelData = isLabel ? labels.find((l) => `LABEL_${l.id}` === r.id) : undefined;
              return (
                <div
                  key={r.id}
                  className={`railItem ${isActive ? "active" : ""} ${isDraggingItem ? "dragging" : ""}`}
                  style={{ position: "relative" }}
                  ref={(el) => {
                    if (el) {
                      itemRefs.current.set(r.id, el);
                    } else {
                      itemRefs.current.delete(r.id);
                    }
                  }}
                  draggable
                  onDragStart={(event) => handleDragStart(event, r.id)}
                  onDragOver={(event) => handleDragOver(event, r.id)}
                  onDrop={(event) => handleDrop(event, r.id)}
                  onDragEnd={handleDragEnd}
                  onClick={() => {
                    if (draggingId) return;
                    setRailId(r.id);
                  }}
                  role="button"
                  tabIndex={0}
                  title={r.label}
                >
                  <div className="railIcon">
                    {r.icon ? <SvgIcon name={r.icon} title={r.label ?? ""} style={{ width: 18, height: 18, display: "block", color: r.color }} /> : null}
                  </div>
                  <div className="railLabel">{r.label}</div>
                  {unread > 0 && r.id !== "PINNED" ? <div className="railBadge">{unread}</div> : null}
                  {isLabel && labelData && (
                    <button
                      className="labelEditBtn"
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleEditLabel(labelData); }}
                      title="Edit label"
                    >
                      <SvgIcon name="edit" title="Edit" style={{ width: 12, height: 12, display: "block" }} />
                    </button>
                  )}
                </div>
              );
            })}
            <button className="railItem" type="button" onClick={handleAddLabel} title="Add label">
              <div className="railIcon">
                <SvgIcon name="plus-circle" title="Add label" style={{ width: 18, height: 18, display: "block" }} />
              </div>
              <span className="railLabel">Add label</span>
            </button>
          </>
        )}
      </div>

      <div className="main">
        <div className="toolbar">
          <div className="searchWrap">
            <input
              className="input search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t(props.locale, "mail.searchPlaceholder")}
              aria-label={t(props.locale, "mail.searchPlaceholder")}
            />
            {searchingServer ? <span className="searchSpinner" aria-label={t(props.locale, "mail.searchingServer")} /> : null}
          </div>

          <div ref={popRef} className="popover accountDropdown" style={{ width: settingsOpen ? "340px" : "280px", padding: settingsOpen ? "16px" : "12px 16px" }}>
            <div
              className="accountHeader"
              onClick={() => setSettingsOpen((v) => !v)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                cursor: "pointer",
                padding: "4px 0",
                userSelect: "none",
                height: "auto"
              }}
            >
              <div style={{ flex: "0 0 32px", width: "32px", height: "32px", borderRadius: "50%", background: "var(--accent)", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", color: "var(--surface)", fontWeight: 700, fontSize: "14px" }}>
                {getInitials()}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                {props.userFullName ? (
                  <>
                    <div style={{ fontWeight: 700, fontSize: "15px", color: "var(--text)", lineHeight: "1.2", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {props.userFullName}
                    </div>
                    <div style={{ fontSize: "12px", color: "var(--accent)", lineHeight: "1.2", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {props.authedEmail}
                    </div>
                  </>
                ) : (
                  <div style={{ fontSize: "12px", color: "var(--accent)", lineHeight: "1.2", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {props.authedEmail}
                  </div>
                )}
              </div>
            </div>

            {settingsOpen ? (
              <>
                <div style={{ height: "1px", background: "var(--line)", margin: "10px 0", opacity: 0.5 }} />

              {/* Account Standing — label + status + score on one line, then bar */}
              <div style={{ marginBottom: "10px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "5px" }}>
                  <RepTooltip text={[
                    "Account standing is measured across five categories. Default score: 5.0.",
                    "OK: Overall ≥4.6 AND all categories ≥3.0 — in good standing.",
                    "WARNING: Overall <4.6 OR any category <3.0 — action needed to improve.",
                    "DANGER: Overall <3.5 OR any category <1.5 — critical risk of suspension or downgrade.",
                  ]}>
                    <div style={{ fontSize: "12px", fontWeight: 600, cursor: "help" }}>Account Standing</div>
                  </RepTooltip>
                  <RepTooltip text={getStatusTooltip(repData)}>
                    <div style={{ fontSize: "11px", color: getStatusColor(repData), fontWeight: 600, cursor: "help" }}>
                      {getReputationStatus(repData)} ({repData.overall.toFixed(1)})
                    </div>
                  </RepTooltip>
                </div>
                <div style={{ width: "100%", height: "6px", background: "var(--hover-bg)", borderRadius: "3px", overflow: "hidden" }}>
                  <div style={{ width: `${(repData.overall / 5) * 100}%`, height: "100%", background: getStatusColor(repData), transition: "all 0.3s" }} />
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "4px" }}>
                  {getReputationStatusMessage(repData) && (
                    <div style={{ fontSize: "10px", color: getStatusColor(repData), fontWeight: 500 }}>
                      {getReputationStatusMessage(repData)}
                    </div>
                  )}
                  <span onClick={() => setRepExpanded(v => !v)} style={{ fontSize: "10px", color: "var(--accent)", cursor: "pointer" }}>
                    {repExpanded ? "Details ∧" : "Details >"}
                  </span>
                </div>
              </div>

              {/* Trust Tier */}
              <div style={{ marginBottom: "10px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "3px" }}>
                  <RepTooltip text={[
                    "Trust tiers progress based on account age and sustained good behaviour. Higher tiers provide access to better sending IP pools and reduce the impact of penalties.",
                    `– ${tierNames[0]}: Starting tier for all accounts\n– ${tierNames[1]}: 1+ month with clean behaviour, better pool (max tier for free accounts)\n– ${tierNames[2]}: 3+ months on ${tierNames[1]}, paid subscription required\n– ${tierNames[3]}: Reserved for future progression (event-driven)\n– ${tierNames[4]}: Reserved for future progression (event-driven, paid only)`,
                    "To advance: maintain overall score ≥4.6 AND all categories ≥3.0 (OK status). To avoid downgrade: keep overall ≥3.5 and all categories ≥1.5.",
                  ]}>
                    <div style={{ fontSize: "11px", color: "var(--muted)", cursor: "help" }}>
                      Trust Tier: <span style={{ fontWeight: 600, color: "var(--text)" }}>{repData.tier}</span>
                    </div>
                  </RepTooltip>
                </div>
                {repData.tier === "Established" && (
                  <div style={{ fontSize: "10px", color: "var(--muted)" }}>
                    Free accounts are limited
                  </div>
                )}
              </div>

              {repExpanded && <>
              <div style={{ height: "1px", background: "var(--line)", margin: "10px 0", opacity: 0.5 }} />

              {/* Five category bars */}
              <div style={{ marginBottom: "10px" }}>
                <div style={{ fontSize: "12px", fontWeight: 600, marginBottom: "8px" }}>Category Scores</div>
                {(
                  [
                    ["Activity", repData.categories.activity, ["Measures your sending and reading patterns compared to your own historical baseline.", "Sudden changes in behaviour — volume spikes, unusual timing, or inactivity — can affect this score."]],
                    ["Content Quality", repData.categories.contentQuality, ["Tracks recipient reactions: spam reports and bounces.", "High complaint or bounce rates will reduce this score and hurt your deliverability."]],
                    ["Behaviour", repData.categories.behaviour, ["Monitors usage patterns: geographic consistency, send timing patterns, and automation detection.", "Erratic or inconsistent patterns compared to your norm can trigger penalties."]],
                    ["Account History", repData.categories.accountHistory, ["Reflects your account age, time spent in each tier, and any prior incidents weighted by how recently they occurred. Repeated violations may result in permanent marks that affect long-term scoring.", "Older accounts with clean histories carry more resilience against isolated incidents."]],
                    ["Reputation", repData.categories.reputation, ["A manual override category used for anomalies outside algorithmic detection, such as suspected coordinated spam.", "A score below 5.0 here indicates a flag has been applied by the system or support team."]],
                  ] as [string, number, string[]][]
                ).map(([label, score, tip]) => (
                  <RepTooltip key={label} text={tip} block>
                    <div style={{ marginBottom: "6px", padding: "3px 4px", borderRadius: "4px", cursor: "help", transition: "background-color 150ms ease" }}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "var(--hover-bg)"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "3px" }}>
                        <div style={{ fontSize: "11px", color: "var(--muted)" }}>{label}</div>
                        <div style={{ fontSize: "11px", color: scoreColor(score), fontWeight: 500 }}>{score.toFixed(1)}</div>
                      </div>
                      <div style={{ width: "100%", height: "4px", background: "var(--hover-bg)", borderRadius: "2px", overflow: "hidden" }}>
                        <div style={{ width: `${(score / 5) * 100}%`, height: "100%", background: scoreColor(score), transition: "all 0.3s" }} />
                      </div>
                    </div>
                  </RepTooltip>
                ))}
              </div>
              </>}

              <div style={{ height: "1px", background: "var(--line)", margin: "10px 0" }} />

              <div className="popRow" onClick={() => { window.history.pushState({}, "", "/settings"); window.dispatchEvent(new PopStateEvent("popstate")); setSettingsOpen(false); }} style={{ gap: "8px", cursor: "pointer", alignItems: "center" }}>
                <SvgIcon name="gear" title={t(props.locale, "settings.account")} className="popRowIcon" />
                <div className="popTitle">{t(props.locale, "settings.account")}</div>
              </div>

              <div style={{ height: "1px", background: "var(--line)", margin: "8px auto", width: "80%", opacity: 0.5 }} />

              <div
                className="popRow"
                onClick={props.onLogout}
                style={{ gap: "8px", cursor: "pointer", alignItems: "center", background: "rgba(239, 68, 68, 0.05)", transition: "background-color 0.2s" }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(239, 68, 68, 0.1)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(239, 68, 68, 0.05)")}
              >
                <SvgIcon name="logout" title={t(props.locale, "settings.signOut")} className="popRowIcon" />
                <div className="popTitle">{t(props.locale, "settings.signOut")}</div>
              </div>
              </>
            ) : null}
          </div>
        </div>

        <div className="listHeader">
          <div>
            <div className="title">{listTitle}</div>
            <div className="small">{filtered.length} {t(props.locale, "mail.messages")}</div>
          </div>
        </div>

        <div className="tableHead">
          <div></div>
          <div>RECEIVED AT</div>
          <div>FROM</div>
          <div>SUBJECT</div>
        </div>

        <div className="list">
          {showLoading ? (
            <div className="emptyState">
              <div className="emptyTitle" style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "center" }}>
                <span className="spinner" />
                Loading messages, please wait…
              </div>
            </div>
          ) : showEmpty ? (
            <div className="emptyState">
              <div className="emptyTitle">{t(props.locale, "mail.emptyTitle")}</div>
              <div className="emptyText">{t(props.locale, "mail.emptyText")}</div>
            </div>
          ) : null}

          {filtered.map((m) => {
            const isActive = m.id === activeId && props.openMode === "in-list";
            const isPinned = m.flagged === true;
            return (
              <div key={m.id} className={`mailItemWrap ${isActive ? "isOpen" : ""} ${isPinned ? "isPinned" : ""}`}>
                <div
                  ref={isActive ? activeRowRef : null}
                  className={`rowMail ${isPinned ? "pinned" : ""} ${isActive ? "active" : ""}`}
                  onClick={() => onRowClick(m.id)}
                  role="button"
                  tabIndex={0}
                  title={m.snippet}
                >
                  <div className="cellStar" onClick={(e) => e.stopPropagation()}>
                    <button
                      className={`starBtn ${isPinned ? "on" : ""}`}
                      title={isPinned ? t(props.locale, "row.unpin") : t(props.locale, "row.pin")}
                      onClick={() => togglePinned(m.id)}
                    >
                        {isPinned ? <SvgIcon className="iconSvg" name="star-fill" title={t(props.locale, "row.unpin")} /> : <SvgIcon className="iconSvg" name="star" title={t(props.locale, "row.pin")} /> }
                      </button>
                  </div>
                  <div className="cellDate mono">{formatDateIso(m.dateIso)}</div>
                  <div className="cellFrom">{m.from}</div>

                  <div className="subjectWrap">
                    <div className={`cellSubject ${m.unread ? "unread" : ""} subjectText`}>
                      {m.unread ? <span className="unreadDot" /> : null}
                      {m.subject}
                    </div>

                    <div style={{ flex: 1 }} />

                    <div className="rowActions" onClick={(e) => e.stopPropagation()}>
                      <div className="kebabWrap">
                        <button
                          className="iconBtn"
                          title={t(props.locale, "row.more")}
                          onClick={() => setMenuOpenId((cur) => (cur === m.id ? null : m.id))}
                        >
                          <SvgIcon className="iconSvg" name="three-dots-vertical" title={t(props.locale, "row.more")} />
                        </button>

                        {menuOpenId === m.id ? (
                          <div ref={menuRef} className="kebabMenu">
                            <div className="menuTitle">{t(props.locale, "menu.moveTo")}</div>
                            <div className="menuItem" onClick={() => onMenuAction("move:inbox", m)}>{t(props.locale, "menu.inbox")}</div>
                            <div className="menuItem" onClick={() => onMenuAction("move:sent", m)}>{t(props.locale, "menu.sent")}</div>
                            <div className="menuItem" onClick={() => onMenuAction("move:drafts", m)}>{t(props.locale, "menu.drafts")}</div>
                            <div className="menuItem" onClick={() => onMenuAction("move:spam", m)}>{t(props.locale, "menu.spam")}</div>
                            <div className="menuItem" onClick={() => onMenuAction("move:trash", m)}>{t(props.locale, "menu.trash")}</div>
                            <div className="menuTitle" style={{ marginTop: 6 }}>SECURITY</div>
<div className="menuItem" onClick={() => onMenuAction("block-sender", m)}>{t(props.locale, "menu.blockSender")}</div>
<div className="menuItem" onClick={() => markUnread(m.id)}>Mark as unread</div>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>

                {props.openMode === "in-list" ? (
                  <div className="expandWrap" style={{ maxHeight: isActive ? "2000px" : "0px" }}>
                    {isActive ? (
                      <div className="expandInner">
                        <div className="expandHeader" onClick={(e) => e.stopPropagation()}>
  <div className="expandHeaderLeft">
  <div className="expandHeaderMetaGrid small">
    <button
      className={`starBtn readerStarBtn ${((full?.flagged ?? m.flagged) === true) ? "on" : ""}`}
      title={((full?.flagged ?? m.flagged) === true) ? t(props.locale, "row.unpin") : t(props.locale, "row.pin")}
      onClick={() => togglePinned(String(full?.uid ?? m.id))}
    >
      {((full?.flagged ?? m.flagged) === true)
        ? <SvgIcon className="iconSvg" name="star-fill" title={t(props.locale, "row.unpin")} />
        : <SvgIcon className="iconSvg" name="star" title={t(props.locale, "row.pin")} />}
    </button>

    <div className="expandHeaderMetaLeft">
      <div className="metaLine">
        <b>{t(props.locale, "reader.from")}:</b>&nbsp;{full?.from ?? m.from}
      </div>
      <div className="metaLine">
        <b>{t(props.locale, "reader.to")}:</b>&nbsp;{full?.to ?? ""}
      </div>
    </div>

    <div className="expandHeaderMetaRight">
      <div className="metaLine">
        <b>{t(props.locale, "reader.date")}:</b>&nbsp;{new Date((full?.dateIso ?? m.dateIso)).toLocaleString()}
      </div>
      <div className="metaAgo mono">{timeAgo(full?.dateIso ?? m.dateIso)}</div>
    </div>
  </div>
</div>
<div style={{ flex: 1 }} />
  <button className="iconBtn readerIconBtn" onClick={() => props.onOpenMessage(m.id)} title={t(props.locale, "reader.openInNewTab")} aria-label={t(props.locale, "reader.openInNewTab")}>
  <SvgIcon className="iconSvg" name="window-stack" title={t(props.locale, "reader.openInNewTab")} />
</button>
<button className="iconBtn readerIconBtn" onClick={printInline} title="Print" aria-label="Print">
  <SvgIcon className="iconSvg" name="printer" title="Print" />
</button>
<div className="kebabWrap" onClick={(e) => e.stopPropagation()}>
  <button
    className="iconBtn readerIconBtn"
    title={t(props.locale, "row.more")}
    aria-label={t(props.locale, "row.more")}
    onClick={() => setReaderMenuOpen((v) => !v)}
  >
    <SvgIcon className="iconSvg" name="three-dots-vertical" title={t(props.locale, "row.more")} />
  </button>
  {readerMenuOpen ? (
    <div ref={menuRef} className="kebabMenu">
      <div className="menuTitle">ACTIONS</div>
      <div className="menuItem" onClick={() => { void markUnread(m.id); setReaderMenuOpen(false); }}>Mark as unread</div>
      <div className="menuItem" onClick={() => { printInline(); setReaderMenuOpen(false); }}>Print</div>
      <div className="menuItem" onClick={() => { props.onOpenMessage(m.id); setReaderMenuOpen(false); }}>Open in new tab</div>
    </div>
  ) : null}
</div>
<button className="iconBtn readerIconBtn" onClick={closeInline} title={t(props.locale, "reader.close")} aria-label={t(props.locale, "reader.close")}>
  <SvgIcon className="iconSvg" name="x-circle" title={t(props.locale, "reader.close")} />
</button>
</div>

                        <div className="expandBody" onClick={(e) => e.stopPropagation()}>
                          {full ? (
                            <>
                              <p className="expandTitle">{full.subject}</p>
                              {full.hasBlockedExternal ? (
                                <div style={{ marginBottom: 10, border: "1px solid var(--line)", borderRadius: 8, overflow: "hidden", fontSize: 13 }}>
                                  <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 14px", background: "rgba(45,140,255,0.06)" }}>
                                    <span style={{ flex: 1, color: "var(--muted)" }}>{showExternalImages ? "External images are visible." : "External images blocked for privacy."}</span>
                                    <button
                                      style={{ padding: "4px 10px", fontSize: 12, color: "var(--accent)", background: "none", border: "1px solid var(--accent)", borderRadius: 4, cursor: "pointer", whiteSpace: "nowrap" }}
                                      onClick={() => setExternalDropdownOpen((v) => !v)}
                                    >
                                      {externalDropdownOpen ? "Hide settings" : "Image settings"}
                                    </button>
                                  </div>
                                  {externalDropdownOpen ? (
                                    <div style={{ borderTop: "1px solid var(--line)", padding: "4px 0" }}>
                                      {[
                                        { label: "Show images for this email", on: showExternalImages, onClick: () => setShowExternalImages((v) => !v) },
                                        { label: `Always trust ${full.from}`, on: trustedSender, onClick: (e: React.MouseEvent) => { e.preventDefault(); void toggleTrustSender(); } },
                                        ...(full.from.includes("@") ? [{ label: `Always trust @${full.from.split("@")[1]}`, on: trustedDomain, onClick: (e: React.MouseEvent) => { e.preventDefault(); void toggleTrustDomain(); } }] : [])
                                      ].map((item, i) => (
                                        <div
                                          key={i}
                                          onClick={item.onClick}
                                          style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "8px 14px", cursor: "pointer" }}
                                          onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = "rgba(45,140,255,0.05)"; }}
                                          onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = ""; }}
                                        >
                                          <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.label}</span>
                                          <span style={{
                                            position: "relative", display: "inline-block", width: 32, height: 18, flexShrink: 0,
                                            background: item.on ? "var(--accent)" : "var(--line)", borderRadius: 9, transition: "background 0.2s"
                                          }}>
                                            <span style={{
                                              position: "absolute", top: 2, left: item.on ? 16 : 2, width: 14, height: 14,
                                              background: "#fff", borderRadius: "50%", boxShadow: "0 1px 2px rgba(0,0,0,0.2)", transition: "left 0.2s"
                                            }} />
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  ) : null}
                                </div>
                              ) : null}
                              <div className="reader" onClick={(e) => {
                                const anchor = (e.target as HTMLElement).closest?.("a[href]") as HTMLAnchorElement | null;
                                if (!anchor) return;
                                const href = anchor.getAttribute("href") ?? "";
                                if (href.startsWith("mailto:")) return; // allow mailto: natively
                                e.preventDefault();
                                e.stopPropagation();
                                setLinkConfirm({ url: href });
                              }}>
                                {full.html && (/<table[\s>]/i.test(full.html) || /<img[\s>]/i.test(full.html) || /<style[\s>]/i.test(full.html) || !full.text) ? (
                                  <div
                                    className="htmlBody"
                                    style={{ background: "#fff", color: "#000", padding: 16, borderRadius: 6 }}
                                    dangerouslySetInnerHTML={{
                                      __html: showExternalImages
                                        ? full.html.replace(/data-blocked-src="([^"]*)"/g, 'src="$1"')
                                        : full.html
                                    }}
                                  />
                                ) : (
                                  <pre>{full.text}</pre>
                                )}
                              </div>

                              {full.attachments && full.attachments.length > 0 ? (
                                <div className="attachBox" onClick={(e) => e.stopPropagation()}>
                                  <div className="small" style={{ fontWeight: 700, marginBottom: 6 }}>Attachments</div>
                                  {full.attachments.map((a) => (
                                    <div
                                      key={a.id}
                                      className="attachRow attachRowClick"
                                      role="button"
                                      tabIndex={0}
                                      title="Download"
                                      onClick={() => onDownloadAttachment(a.id, a.filename)}
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter" || e.key === " ") {
                                          e.preventDefault();
                                          onDownloadAttachment(a.id, a.filename);
                                        }
                                      }}
                                    >
                                      <div className="attachIcon" aria-hidden="true">⤓</div>
                                      <div className="attachName">{a.filename ?? a.id}</div>
                                      <div style={{ flex: 1 }} />
                                    </div>
                                  ))}
                                </div>
                              ) : null}
                              <div className="readerActions" onClick={(e) => e.stopPropagation()}>
                                <button className="btn" onClick={() => openReply("reply")}>
                                  {t(props.locale, "reader.reply")}
                                </button>
                                {full?.to && full.to.includes(",") ? (
                                  <button className="btn" onClick={() => openReply("replyAll")}>
                                    {t(props.locale, "reader.replyAll")}
                                  </button>
                                ) : null}
                                <button className="btn" onClick={() => openReply("forward")}>
                                  {t(props.locale, "reader.forward")}
                                </button>
  <div style={{ flex: 1 }} />
</div>
                            </>
                          ) : (
                            <div className="small">Loading...</div>
                          )}
                        </div>
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            );
          })}
      </div>

      <button className="fabCompose" onClick={openCompose}>
        <span className="fabComposeIcon"><SvgIcon name="pencil" title="" style={{ width: 20, height: 20 }} /></span>
        <span className="fabComposeLabel">{t(props.locale, "mail.compose")}</span>
      </button>

      <ConfirmModal
        isOpen={linkConfirm !== null}
        title={t(props.locale, "link.warn.title")}
        body={`${t(props.locale, "link.warn.body")}\n\n${linkConfirm?.url ?? ""}`}
        confirmText={t(props.locale, "link.warn.proceed")}
        cancelText={t(props.locale, "link.warn.cancel")}
        onConfirm={() => { window.open(linkConfirm!.url, "_blank", "noopener,noreferrer"); setLinkConfirm(null); }}
        onCancel={() => setLinkConfirm(null)}
      />

      <ConfirmModal
        isOpen={attachConfirm !== null}
        title={t(props.locale, "attach.warn.title")}
        body={t(props.locale, "attach.warn.body")}
        confirmText={t(props.locale, "attach.warn.proceed")}
        cancelText={t(props.locale, "attach.warn.cancel")}
        onConfirm={() => { startDownload(attachConfirm!.url); setAttachConfirm(null); }}
        onCancel={() => setAttachConfirm(null)}
      />

      <ComposeModal
        locale={props.locale}
        fromEmail={props.authedEmail}
        isOpen={composeOpen}
        onClose={closeCompose}
        onSaveDraft={saveDraftFromCompose}
        onSend={sendFromCompose}
        initialTo={composePrefill?.to}
        initialSubject={composePrefill?.subject}
        initialText={composePrefill?.text}
        contacts={contacts}
      />

      <LabelModal
        isOpen={labelModalOpen}
        onClose={() => setLabelModalOpen(false)}
        onSave={handleLabelSaved}
        onDelete={() => { refreshLabels(); if (editingLabel?.id) setRailId("INBOX"); }}
        existingLabel={editingLabel}
      />
    </div>
  </div>
  );
}
